import { createContext } from "react";
import {
  Action,
  AppContextType,
  AppState,
  PharmacyType,
  PrescriptionDrug,
  UserLanguage,
  LowIncomeSubsidyStatus,
  PlanSNPType,
  EventTrackingPayload,
  IRAInsulinCoachmarkType,
} from "../../@types";

import { saveBeneDrugs, saveBenePharmacies } from "../../api";
import { hasLocalSession } from "../../helpers/loginHelpers";
// Do not attempt to import from re-exports (../../helpers) at the moment
// This breaks tests
import { cachedStore } from "../../helpers/storeHelpers";
import { logError } from "../../helpers";
import cookies from "browser-cookies";
import { deduplicatePrescriptions } from "../../helpers/beneficiaryInfoHelpers";
import { ApiError } from "../../helpers/errors";

import * as TypeGuards from "../../@types/guards";

export const defaultState: AppState = {
  beneficiary: undefined,
  compareList: [],
  county: undefined,
  csr: undefined,
  currentCoverage: undefined,
  cwCoverage: undefined,
  degradedIntegrations: [],
  demographicInfo: undefined,
  fips: undefined,
  futureLis: undefined,
  iraInsulinCoachmarkHiddenStates: {
    [IRAInsulinCoachmarkType.PLAN_SEARCH_RESULTS]: false,
    [IRAInsulinCoachmarkType.PLAN_COMPARE]: false,
    [IRAInsulinCoachmarkType.DRUG_LIST]: false,
  },
  language: UserLanguage.ENGLISH,
  lis: undefined,
  mbpHandoffFailed: false,
  mbpReferrerUri: "",
  medigapCoverages: [],
  multipleRolloverPlans: false,
  newToMedicareAlertInfo: undefined,
  nextYearCoverage: undefined,
  oecStatuses: { currentYear: undefined, nextYear: undefined },
  pharmacies: [],
  pharmacyAddress: undefined,
  pharmacyType: PharmacyType.NONE,
  prescriptions: [],
  routeParams: {},
  searchResultsFilters: {
    drugCoverageFilter: undefined,
    insuranceCarrierFilter: "",
    insuranceCarriers: [],
    planCategoriesFilter: [],
    planCoveragesFilter: [],
    sortBy: undefined,
    starRatingFilter: "",
  },
  snpFilters: [],
  tealiumLoaded: false,
  trackedEvents: {},
  zipcode: undefined,
};

export const initialAppState: AppState = cachedStore || defaultState;

initialAppState.language = navigator.language.split(/[-_]/)[0] as UserLanguage;

export const resetState = (): AppState => {
  // Clear session storage relative to domain
  window.sessionStorage.clear();

  // IE 11 shares session ID cookie over multiple windows and tabs, unlike other browsers
  cookies.erase("sid");

  return { ...defaultState };
};

export const updatePrescriptions = async (
  prescriptions: PrescriptionDrug[]
): Promise<void> => {
  if (hasLocalSession()) {
    try {
      saveBeneDrugs(prescriptions);
    } catch (e) {
      logError(
        "Failed to save beneficiary drugs (AppStore/updatePrescriptions)",
        e as ApiError
      );
    }
  }
};

export const setPrescriptions = (
  state: AppState,
  { payload }: Action
): AppState => {
  let prescriptions = TypeGuards.hasPrescriptions(payload)
    ? payload
    : state.prescriptions;

  prescriptions = deduplicatePrescriptions(prescriptions);
  prescriptions.sort((a, b) => a.name.localeCompare(b.name));

  updatePrescriptions(prescriptions);

  return { ...state, prescriptions };
};

export const addPrescription = (
  state: AppState,
  { payload: prescription }: Action
): AppState => {
  const prescriptions = TypeGuards.isPrescription(prescription)
    ? [...state.prescriptions, prescription]
    : state.prescriptions;

  prescriptions.sort((a, b) => a.name.localeCompare(b.name));

  updatePrescriptions(prescriptions);

  return {
    ...state,
    prescriptions,
  };
};

export const editPrescription = (
  state: AppState,
  { payload }: Action
): AppState => {
  if (TypeGuards.hasIndex(payload) && TypeGuards.hasDrug(payload)) {
    const { index, drug } = payload;

    const prescriptions = state.prescriptions.map((prescription, i) => {
      if (i !== index) {
        return prescription;
      }

      return {
        ...prescription,
        ...drug,
      };
    });

    updatePrescriptions(prescriptions);

    return {
      ...state,
      prescriptions,
    };
  }

  return state;
};

export const deletePrescription = (
  state: AppState,
  { payload }: Action
): AppState => {
  if (TypeGuards.hasIndex(payload)) {
    const prescriptions = state.prescriptions.filter(
      (p, i) => i !== payload.index
    );

    updatePrescriptions(prescriptions);

    return {
      ...state,
      prescriptions,
    };
  }

  return state;
};

export const hasMailOrderPharmacy = (state: AppState): boolean => {
  return [PharmacyType.MAIL, PharmacyType.MAIL_AND_RETAIL].includes(
    state.pharmacyType
  );
};

export const addPharmacy = (
  state: AppState,
  { payload: pharmacy }: Action
): AppState => {
  if (TypeGuards.isPharmacy(pharmacy)) {
    const pharmacies = [...state.pharmacies, pharmacy];
    return { ...state, pharmacies };
  }

  return state;
};

export const deletePharmacy = (
  state: AppState,
  { payload: npi }: Action
): AppState => {
  if (typeof npi === "string") {
    const pharmacies = state.pharmacies.filter(p => p.npi !== npi);
    return {
      ...state,
      pharmacies,
    };
  }
  return state;
};

export const savePharmacies = (state: AppState): AppState => {
  if (hasLocalSession()) {
    const npis = state.pharmacies.map(p => p.npi);
    if (hasMailOrderPharmacy(state)) {
      npis.push("mail-order");
    }
    try {
      saveBenePharmacies(npis);
    } catch (e) {
      logError(
        "Failed to save beneficiary pharmacies (AppStore/savePharmacies)",
        e as ApiError
      );
    }
  }
  return state;
};

export const setPharmacies = (
  state: AppState,
  { payload }: Action
): AppState => {
  const pharmacies = TypeGuards.hasPharmacies(payload)
    ? payload
    : state.pharmacies;

  return { ...state, pharmacies };
};

export const setMailOrderPharmacyNetworkStatus = (
  state: AppState,
  { payload }: Action
): AppState => {
  const mailOrderNetworkStatus = TypeGuards.isMailOrderPharmacyStatus(payload)
    ? payload
    : state.mailOrderNetworkStatus;
  return { ...state, mailOrderNetworkStatus };
};

export const updateIraInsulinCoachmarkHiddenStates = (
  state: AppState,
  { payload }: Action
): AppState => {
  const iraInsulinCoachmark = TypeGuards.isIRAInsulinCoachmarkType(payload)
    ? payload
    : undefined;
  if (iraInsulinCoachmark) {
    return {
      ...state,
      iraInsulinCoachmarkHiddenStates: {
        ...state.iraInsulinCoachmarkHiddenStates,
        [iraInsulinCoachmark]: true,
      },
    };
  }
  return state;
};

export const updateLis = (
  state: AppState,
  { payload: lis }: Action,
  setFutureLis = false
): AppState => {
  if (!TypeGuards.isLIS(lis)) {
    return state;
  }

  const snpFilters = state.snpFilters || [];

  if (
    [
      LowIncomeSubsidyStatus.LIS_LEVEL_1A,
      LowIncomeSubsidyStatus.LIS_LEVEL_2,
      LowIncomeSubsidyStatus.LIS_LEVEL_3,
    ].includes(lis)
  ) {
    if (!Object.values(snpFilters).includes(PlanSNPType.SNP_TYPE_NOT_SNP)) {
      snpFilters.push(PlanSNPType.SNP_TYPE_NOT_SNP);
    }

    if (
      !Object.values(snpFilters).includes(PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE)
    ) {
      snpFilters.push(PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE);
    }
  }

  return setFutureLis
    ? { ...state, futureLis: lis, snpFilters }
    : { ...state, lis, snpFilters };
};

export const updatePlanType = (
  state: AppState,
  { payload }: Action
): AppState => {
  const planType = TypeGuards.isPlanType(payload) ? payload : undefined;

  return {
    ...state,
    planType,
    searchResultsFilters: defaultState.searchResultsFilters,
  };
};

/**
 * Updates the plan-specific network status for a pharmacy. Called whenever
 * `PharmacyNetworkStatus` is used, to prevent flash-of-incorrect-state issues
 * when switching between plans
 */
export const updatePharmacyNetworkStatus = (
  state: AppState,
  { payload }: Action
): AppState => {
  if (TypeGuards.isPharmacyInfo(payload)) {
    const pharmacies = state.pharmacies.map(p => {
      return p.npi === payload.npi
        ? { ...p, in_network: payload.in_network, preferred: payload.preferred }
        : p;
    });
    return {
      ...state,
      pharmacies,
    };
  }
  return state;
};

export const updateSearchResultsFiltersCarriers = (
  state: AppState,
  { payload }: Action
): AppState => {
  const insuranceCarriers = TypeGuards.hasInsuranceCarriers(payload)
    ? payload
    : [];

  return {
    ...state,
    searchResultsFilters: {
      ...state.searchResultsFilters,
      insuranceCarriers,
    },
  };
};

export const updateTrackedEvents = (
  state: AppState,
  { payload }: Action
): AppState => {
  const eventTrackingPayload = payload as EventTrackingPayload;
  const eventToRegister = TypeGuards.isRegisteredEvent(
    eventTrackingPayload?.event
  )
    ? eventTrackingPayload.event
    : null;
  if (eventToRegister && typeof eventTrackingPayload?.value !== "undefined") {
    return {
      ...state,
      trackedEvents: {
        ...state.trackedEvents,
        [eventToRegister]: eventTrackingPayload.value,
      },
    };
  }
  return state;
};

export const AppContext = createContext({} as AppContextType);
